import type { AbbreviatedAccountsListQueryVariables } from '@/graphql/generated/schema'
import type {
  AccountsListQueryVariables as BaseAccountsListQueryVariables,
  GetAccountsQuery,
  GetAccountsQueryVariables,
  GetAdvisorsQuery,
  GetAdvisorsQueryVariables,
  PositionsForInstrumentQuery,
  PositionsForInstrumentQueryVariables,
} from '@/graphql/generated/schema.pre-account-migration'
import type {
  GetAbbreviatedAccountListResponse,
  GetAccountListResponse,
} from '@/hooks/use-get-abbreviated-account-data'
import type {
  AccountDetail,
  AccountStatistics,
  AccountStatisticsRequest,
  AccountsPlaceBlockOrderResponse,
  AccountUpdateRequest,
  BatchReassignTransactionsParams,
  BulkUpdateAccountRestrictionsRequestBody,
  BulkUpdateAccountRestrictionsResponse,
  CreateSleeveParams,
  CreateSleeveResponse,
  ReassignTransactionsParams,
  SetWashSaleOverrideRequestBody,
  SetWashSaleOverrideResponse,
  SleeveHoldingsAssignmentParams,
  SleeveHoldingsAssignmentResponse,
  SleeveIdentifierParams,
  SleeveModelAssignmentParams,
  SleeveModelAssignmentResponse,
  TransactionReassignmentResponse,
  UpdateAccountRestrictionsRequestBody,
  UpdateAccountRestrictionsResponse,
  UpdateAccountSettingsRequest,
  UpdateAccountSettingsResponse,
  UpdateLotParams,
  UpdateSleeveParams,
} from '@/types/api/accounts'
import type {
  CashRaise,
  CreateCashRaiseRequest,
  UpdateCashRaiseRequest,
} from '@/types/api/cash-raise'
import type {
  RedistributeCashRaisesRequest,
  RedistributeCashRaisesResponse,
  RedistributePreviewResponse,
} from '@/types/api/cash-raise-redistribute'
import type {
  CashRaiseRecurrenceOptionsResponse,
  CashRaiseSchedule,
  CashRaiseSchedulePreviewRequest,
  CashRaiseSchedulePreviewResponse,
  CreateCashRaiseScheduleRequest,
  DeleteCashRaiseScheduleResponse,
  EditCashRaiseScheduleRequest,
  EditCashRaiseScheduleResponse,
} from '@/types/api/cash-raise-schedule'
import type {
  CashRebalancePreviewResponse,
  CreateCashRebalanceRequest,
  CreateCashRebalanceResponse,
  ManualCashRebalanceRequest,
  ManualCashRebalanceResponse,
} from '@/types/api/cash-rebalance'
import type {
  CreateCashTransferRequest,
  CreateCashTransferResponse,
} from '@/types/api/cash-transfer'
import type { AccountHoldingsResponse } from '@/types/api/holdings'
import type { ProjectedCashResponse } from '@/types/api/projected-cash'
import type {
  CreateRemediationLotsRequestBody,
  CreateRemediationLotsResponse,
} from '@/types/api/remediation-lots'
import type {
  SleeveRealignmentBulkActionRequest,
  SleeveRealignmentBulkActionResponse,
  SleeveRealignmentCancelPreview,
  SleeveRealignmentInitiateRequest,
  SleeveRealignmentPreview,
  SleeveRealignmentRebalancePreview,
  SleeveRealignmentSourceScope,
  SleeveRealignmentStatus,
} from '@/types/api/sleeve-realignment'

import {
  ABBREVIATED_ACCOUNT_LIST_QUERY,
  ABBREVIATED_ACCOUNT_LIST_WITH_MARGIN_QUERY,
  ACCOUNT_IDS_QUERY,
  ACCOUNT_LIST_QUERY,
  ACCOUNT_LIST_WITH_MARGIN_QUERY,
  buildAccountDntStatusQuery,
} from '@/graphql/queries/account-list'
import {
  ACCOUNTS_QUERY,
  ADVISORS_QUERY,
  POSITIONS_FOR_INSTRUMENT_QUERY,
} from '@/graphql/queries/holdings'
import { minCashObjectFromSettings } from '@/portfolio/shared/account-sections/components/minimum-cash-helpers'
import { DEFAULT_SOURCE_SCOPE } from '@/types/api/sleeve-realignment'
import { TagTypes } from '@/types/api/tag-types'

import { baseApiSlice } from './base-api-slice'
import { normalizeCashRebalanceResponse } from './helpers/normalize-cash-rebalance-response'

type AccountsListQueryVariables = BaseAccountsListQueryVariables & {
  include_margin_fields?: boolean
}

export const ABBREVIATED_ACCOUNT_LIST_QUERY_LIMIT = 10_000

// Every realignment action moves cash between sleeves, so the account's
// holdings and sleeve rows go stale alongside the realignment itself.
function realignmentActionTags(accountIds: string[]) {
  return [
    ...accountIds.flatMap((accountId) => [
      { type: TagTypes.SleeveRealignment as const, id: accountId },
      { type: TagTypes.Accounts as const, id: accountId },
    ]),
    TagTypes.AccountHoldings,
  ]
}

const accountsSlice = baseApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // GraphQL endpoint for account list which will eventually replace getAccounts
    // https://withmoment.postman.co/workspace/Quant-Team~b9391172-de9e-4156-9e3b-47f53b916222/graphql-request/67ec4b57c8390d74b847ac82?action=share&creator=33566697&ctx=documentation&active-environment=31283214-b896c595-a35b-49f7-893a-ea1e72114679

    getAccountList: builder.query<
      GetAccountListResponse,
      AccountsListQueryVariables
    >({
      query: (params) => {
        const queryString = params.include_margin_fields
          ? ACCOUNT_LIST_WITH_MARGIN_QUERY
          : ACCOUNT_LIST_QUERY

        return {
          url: 'account/query/',
          method: 'POST',
          body: {
            query: queryString,
            variables: {
              limit: params?.limit || 100, // Default limit to 100
              offset: params?.offset || 0, // Default offset to 0
              filters: params?.filters,
              sort: params?.sort,
            },
          },
        }
      },
      providesTags: [TagTypes.Accounts],
    }),

    // Lightweight query that fetches account IDs and sleeve IDs (no statistics, etc.)
    getAccountIds: builder.query<
      {
        data: {
          accounts: {
            accounts: Array<{
              moment_account_id: string
              account_name?: string
              sleeves: Array<{
                moment_sleeve_id: string
                is_default: boolean
                model_assignment?: {
                  model?: { id: string }
                }
              }>
            }>
            total_count: number
          }
        }
      },
      AccountsListQueryVariables
    >({
      query: (params) => ({
        url: 'account/query/',
        method: 'POST',
        body: {
          query: ACCOUNT_IDS_QUERY,
          variables: {
            limit: params?.limit || 100,
            offset: params?.offset || 0,
            filters: params?.filters,
            sort: params?.sort,
          },
        },
      }),
      providesTags: [TagTypes.Accounts],
    }),

    getAbbreviatedAccountList: builder.query<
      GetAbbreviatedAccountListResponse,
      AbbreviatedAccountsListQueryVariables
    >({
      query: (
        params = {
          limit: ABBREVIATED_ACCOUNT_LIST_QUERY_LIMIT,
          offset: 0,
          include_sleeves: false,
          include_statistics: true,
          include_margin_fields: false,
        },
      ) => {
        const queryString = params.include_margin_fields
          ? ABBREVIATED_ACCOUNT_LIST_WITH_MARGIN_QUERY
          : ABBREVIATED_ACCOUNT_LIST_QUERY

        return {
          url: 'account/query/',
          method: 'POST',
          body: {
            query: queryString,
            variables: {
              limit: params.limit,
              offset: params.offset,
              filters: params.filters,
              sort: params.sort,
              include_sleeves: params.include_sleeves,
              include_statistics: params.include_statistics,
            },
          },
        }
      },
      providesTags: [TagTypes.Accounts],
    }),

    // Fetch trading-status fields (DNT/DNB/DNS booleans + DNT reason, plus future
    // moment_status / min_cash_validation) for a list of account IDs.
    // TODO: add moment_status and min_cash_validation
    getAccountStatuses: builder.query<
      Array<{
        moment_account_id: string
        do_not_trade?: boolean
        do_not_buy?: boolean
        do_not_sell?: boolean
        do_not_trade_reason?: string
      }>,
      { accountIds: string[]; includeMarginFields: boolean }
    >({
      query: ({ accountIds, includeMarginFields }) => ({
        url: 'account/query/',
        method: 'POST',
        body: {
          query: buildAccountDntStatusQuery(includeMarginFields),
          variables: { accountIds },
        },
      }),
      transformResponse: (response: {
        data?: {
          accounts: {
            accounts: Array<{
              moment_account_id: string
              do_not_trade?: boolean
              do_not_buy?: boolean
              do_not_sell?: boolean
              do_not_trade_reason?: string
            }>
          }
        }
        accounts?: {
          accounts: Array<{
            moment_account_id: string
            do_not_trade?: boolean
            do_not_buy?: boolean
            do_not_sell?: boolean
            do_not_trade_reason?: string
          }>
        }
      }) => {
        const accountsData = response.data?.accounts
        return accountsData?.accounts || []
      },
    }),

    getAccountById: builder.query<AccountDetail, string>({
      query: (id) => ({
        url: `account/${id}/`,
        method: 'GET',
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      providesTags: (result, error, id) => [{ type: TagTypes.Accounts, id }],
    }),

    getAccountStatistics: builder.query<
      AccountStatistics,
      AccountStatisticsRequest
    >({
      query: ({ id, start_date, end_date, frequency }) => ({
        url: `account/${id}/stats/`,
        method: 'GET',
        params: {
          start_date,
          end_date,
          frequency,
        },
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      providesTags: (result, error, id) => [
        { type: TagTypes.Accounts, id: id.id },
      ],
    }),

    cancelAccountOrder: builder.mutation<
      void,
      { accountId: string; orderId: string }
    >({
      query: ({ accountId, orderId }) => ({
        url: `account/${accountId}/order/${orderId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.Accounts],
    }),

    getOrders: builder.query<
      AccountsPlaceBlockOrderResponse[],
      { accountId: string; status?: string; side?: string }
    >({
      query: ({ accountId, status, side }) => ({
        url: `account/${accountId}/order/`,
        method: 'GET',
        params: { status, side },
      }),
      transformResponse: (response: {
        data: AccountsPlaceBlockOrderResponse[]
      }) => response.data,
      providesTags: [TagTypes.Accounts],
    }),

    playAccount: builder.mutation<void, string>({
      query: (id) => ({
        url: `account/${id}/status/play/`,
        method: 'PUT',
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, id) => [{ type: TagTypes.Accounts, id }],
    }),

    pauseAccount: builder.mutation<void, string>({
      query: (id) => ({
        url: `account/${id}/status/pause/`,
        method: 'PUT',
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, id) => [{ type: TagTypes.Accounts, id }],
    }),

    updateAccount: builder.mutation<
      void,
      { id: string; updates: AccountUpdateRequest }
    >({
      query: ({ id, updates }) => ({
        url: `account/${id}/`,
        method: 'PATCH',
        body: updates,
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, id) => [
        { type: TagTypes.Accounts, id: id.id },
      ],
    }),

    updateAccountSettings: builder.mutation<
      UpdateAccountSettingsResponse,
      { id: string; body: UpdateAccountSettingsRequest }
    >({
      query: ({ id, body }) => ({
        url: `account/${id}/settings/`,
        method: 'PUT',
        body,
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, { id }) => [
        { type: TagTypes.Accounts, id },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            accountsSlice.util.updateQueryData(
              'getAccountById',
              id,
              (draft) => {
                draft.settings = data.settings
                draft.min_cash = minCashObjectFromSettings(data.settings)
              },
            ),
          )
        } catch {
          // Leave cache unchanged when the mutation fails.
        }
      },
    }),

    getAccountHoldings: builder.query<AccountHoldingsResponse, void>({
      query: () => ({
        url: `account/position/`,
        method: 'GET',
      }),
      providesTags: [TagTypes.AccountHoldings],
    }),

    getAdvisorsFromPositions: builder.query<
      { data: GetAdvisorsQuery },
      GetAdvisorsQueryVariables
    >({
      query: (variables) => ({
        url: 'account/query/',
        method: 'POST',
        body: { query: ADVISORS_QUERY, variables },
      }),
      providesTags: [TagTypes.AccountHoldings],
    }),

    getAccountsFromPositions: builder.query<
      { data: GetAccountsQuery },
      GetAccountsQueryVariables
    >({
      query: (variables) => ({
        url: 'account/query/',
        method: 'POST',
        body: { query: ACCOUNTS_QUERY, variables },
      }),
      providesTags: [TagTypes.AccountHoldings],
    }),

    getPositionsForInstrument: builder.query<
      { data: PositionsForInstrumentQuery },
      PositionsForInstrumentQueryVariables
    >({
      query: (variables) => ({
        url: 'account/query/',
        method: 'POST',
        body: { query: POSITIONS_FOR_INSTRUMENT_QUERY, variables },
      }),
      providesTags: [TagTypes.AccountHoldings],
    }),
    //
    // Sleeve Management
    //
    // https://docs.withmoment.com/reference/createsleeve
    createSleeve: builder.mutation<CreateSleeveResponse, CreateSleeveParams>({
      query: ({ account_id, body }) => ({
        url: `account/${account_id}/sleeve/`,
        method: 'POST',
        body,
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, { account_id }) => [
        TagTypes.Accounts,
        { type: TagTypes.Accounts, id: account_id },
      ],
    }),
    // https://docs.withmoment.com/reference/updatesleeve
    updateSleeve: builder.mutation<void, UpdateSleeveParams>({
      query: ({ account_id, sleeve_id, body }) => ({
        url: `account/${account_id}/sleeve/${sleeve_id}/`,
        method: 'PATCH',
        body,
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, { account_id }) => [
        TagTypes.Accounts,
        { type: TagTypes.Accounts, id: account_id },
      ],
    }),
    // https://docs.withmoment.com/reference/deletesleeve
    deleteSleeve: builder.mutation<void, SleeveIdentifierParams>({
      query: ({ account_id, sleeve_id }) => ({
        url: `account/${account_id}/sleeve/${sleeve_id}/`,
        method: 'DELETE',
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, { account_id }) => [
        TagTypes.Accounts,
        { type: TagTypes.Accounts, id: account_id },
      ],
    }),
    // https://docs.withmoment.com/reference/assignlotstosleeve
    assignHoldingsToSleeve: builder.mutation<
      SleeveHoldingsAssignmentResponse,
      SleeveHoldingsAssignmentParams
    >({
      query: ({ account_id, sleeve_id, body }) => ({
        url: `account/${account_id}/sleeve/${sleeve_id}/assign-lots/`,
        method: 'PUT',
        body: {
          lots: body.lots,
        },
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, { account_id }) => [
        TagTypes.Accounts,
        { type: TagTypes.Accounts, id: account_id },
      ],
    }),
    // Manual transaction routing: moves a transaction's cash effect to a chosen
    // sleeve. Cash only — no custodian feed links a transaction to a lot, so no
    // position moves. Both routes are top-level on accounts-api and are proxied
    // path-as-is, so neither takes a trailing slash (only `/auth/v1` paths get
    // trailing-slash re-routing in public-api).
    reassignTransactions: builder.mutation<
      TransactionReassignmentResponse,
      ReassignTransactionsParams
    >({
      query: (body) => ({
        url: 'transaction-reassignment',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TagTypes.Accounts],
    }),
    // Strategy-driven variant: the caller names strategies, not sleeves, and the
    // backend infers each account's target sleeve.
    batchReassignTransactions: builder.mutation<
      TransactionReassignmentResponse,
      BatchReassignTransactionsParams
    >({
      query: (body) => ({
        url: 'transaction-reassignment/batch',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TagTypes.Accounts],
    }),
    updateLot: builder.mutation<void, UpdateLotParams>({
      query: ({ lot_id, body }) => ({
        url: `lot/${lot_id}/overrides/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [TagTypes.AccountHoldings, TagTypes.Accounts],
    }),
    // https://docs.withmoment.com/reference/assignsleevetomodel
    assignSleeveToModel: builder.mutation<
      SleeveModelAssignmentResponse,
      SleeveModelAssignmentParams
    >({
      query: ({ account_id, sleeve_id, body }) => ({
        url: `account/${account_id}/sleeve/${sleeve_id}/model/`,
        method: 'PUT',
        body,
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, { account_id }) => [
        TagTypes.Accounts,
        { type: TagTypes.Accounts, id: account_id },
      ],
    }),
    // https://docs.withmoment.com/reference/unassignsleevefrommodel
    unassignSleeveFromModel: builder.mutation<void, SleeveIdentifierParams>({
      query: ({ account_id, sleeve_id }) => ({
        url: `account/${account_id}/sleeve/${sleeve_id}/model/`,
        method: 'DELETE',
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, { account_id }) => [
        TagTypes.Accounts,
        { type: TagTypes.Accounts, id: account_id },
      ],
    }),
    // PUT /v1/account/{account_id}/restrictions/
    // https://docs.withmoment.com/reference/updateaccountrestrictions
    updateAccountRestrictions: builder.mutation<
      UpdateAccountRestrictionsResponse,
      { account_id: string; body: UpdateAccountRestrictionsRequestBody }
    >({
      query: ({ account_id, body }) => ({
        url: `account/${account_id}/restrictions/`,
        method: 'PUT',
        body,
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, { account_id }) => [
        TagTypes.Accounts,
        { type: TagTypes.Accounts, id: account_id },
      ],
    }),
    // PUT /v1/account/{account_id}/wash-sale-overrides/
    // Disables (disabled=true) or re-enables (disabled=false) one wash-sale
    // restriction for an instrument.
    setWashSaleOverride: builder.mutation<
      SetWashSaleOverrideResponse,
      { account_id: string; body: SetWashSaleOverrideRequestBody }
    >({
      query: ({ account_id, body }) => ({
        url: `account/${account_id}/wash-sale-overrides/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { account_id }) => [
        TagTypes.Accounts,
        { type: TagTypes.Accounts, id: account_id },
      ],
    }),

    // POST /v1/accounts/restrictions/bulk/
    // Applies the same restriction change to many accounts (all-or-nothing).
    bulkUpdateAccountRestrictions: builder.mutation<
      BulkUpdateAccountRestrictionsResponse,
      BulkUpdateAccountRestrictionsRequestBody
    >({
      query: (body) => ({
        url: `accounts/restrictions/bulk/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [TagTypes.Accounts],
    }),

    // Update account extra data
    updateAccountExtra: builder.mutation<
      void,
      { id: string; extra: Record<string, string | number> }
    >({
      query: ({ id, extra }) => ({
        url: `account/${id}/extra/`,
        method: 'PATCH',
        body: extra,
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, { id }) => [
        { type: TagTypes.Accounts, id },
      ],
    }),

    //
    // Cash Raise Management
    //

    // Server-computed projected settled-cash schedule for an account. The
    // `running_total` on each projection is authoritative — render it as-is.
    getProjectedCash: builder.query<ProjectedCashResponse, string>({
      query: (accountId) => ({
        url: `account/${accountId}/projected-cash/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, accountId) => [
        { type: TagTypes.Accounts, id: accountId },
      ],
    }),

    createCashRaise: builder.mutation<
      CashRaise,
      { account_id: string; body: CreateCashRaiseRequest }
    >({
      query: ({ account_id, body }) => ({
        url: `account/${account_id}/cash-raise/`,
        method: 'POST',
        body,
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, { account_id }) => [
        TagTypes.CashRaise,
        { type: TagTypes.CashRaise, id: account_id },
      ],
    }),

    updateCashRaise: builder.mutation<
      CashRaise,
      {
        account_id: string
        cash_raise_id: string
        body: UpdateCashRaiseRequest
      }
    >({
      query: ({ account_id, cash_raise_id, body }) => ({
        url: `account/${account_id}/cash-raise/${cash_raise_id}/`,
        method: 'PATCH',
        body,
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, _error, { account_id }) => [
        TagTypes.CashRaise,
        { type: TagTypes.CashRaise, id: account_id },
      ],
    }),

    cancelCashRaise: builder.mutation<
      CashRaise,
      { account_id: string; cash_raise_id: string }
    >({
      query: ({ account_id, cash_raise_id }) => ({
        url: `account/${account_id}/cash-raise/${cash_raise_id}/`,
        method: 'DELETE',
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, _error, { account_id }) => [
        TagTypes.CashRaise,
        { type: TagTypes.CashRaise, id: account_id },
        // Canceling an activated raise returns its reserved cash to the source
        // sleeve, so per-sleeve balances change — mirror activateCashRaise.
        { type: TagTypes.Accounts, id: account_id },
        TagTypes.AccountHoldings,
      ],
    }),

    completeCashRaise: builder.mutation<
      CashRaise,
      { account_id: string; cash_raise_id: string }
    >({
      query: ({ account_id, cash_raise_id }) => ({
        url: `account/${account_id}/cash-raise/${cash_raise_id}/complete/`,
        method: 'POST',
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, _error, { account_id }) => [
        TagTypes.CashRaise,
        { type: TagTypes.CashRaise, id: account_id },
      ],
    }),

    // Sleeve-scoped raises are created dormant and must be activated before
    // they can be completed. Legacy (non-sleeve) raises don't have an
    // activate endpoint in public-api — calling this on one returns 501.
    activateCashRaise: builder.mutation<
      CashRaise,
      { account_id: string; cash_raise_id: string }
    >({
      query: ({ account_id, cash_raise_id }) => ({
        url: `account/${account_id}/cash-raise/${cash_raise_id}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { account_id }) => [
        TagTypes.CashRaise,
        { type: TagTypes.CashRaise, id: account_id },
        // Activation transfers cash from the source sleeve to the funding
        // sleeve, so per-sleeve cash balances change. Nudge legacy account
        // caches; sleeve balances re-poll via SORM (useAccountSleeves).
        { type: TagTypes.Accounts, id: account_id },
        TagTypes.AccountHoldings,
      ],
    }),

    // Cash Raise Schedules (recurring cash raises)
    //
    // Every schedule mutation invalidates BOTH tag families: schedule writes
    // materialize / cancel cash_raise_request rows as a side effect.
    createCashRaiseSchedule: builder.mutation<
      CashRaiseSchedule,
      { account_id: string; body: CreateCashRaiseScheduleRequest }
    >({
      query: ({ account_id, body }) => ({
        url: `account/${account_id}/cash-raise-schedule/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { account_id }) => [
        TagTypes.CashRaiseSchedule,
        { type: TagTypes.CashRaiseSchedule, id: account_id },
        TagTypes.CashRaise,
        { type: TagTypes.CashRaise, id: account_id },
        // Create (and this_one detach below) activates the occurrence on the
        // spot when its lead has already arrived, moving cash between sleeves
        // — same nudge as activateCashRaise.
        { type: TagTypes.Accounts, id: account_id },
        TagTypes.AccountHoldings,
      ],
    }),

    editCashRaiseSchedule: builder.mutation<
      EditCashRaiseScheduleResponse,
      {
        account_id: string
        schedule_id: string
        body: EditCashRaiseScheduleRequest
      }
    >({
      query: ({ account_id, schedule_id, body }) => ({
        url: `account/${account_id}/cash-raise-schedule/${schedule_id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { account_id }) => [
        TagTypes.CashRaiseSchedule,
        { type: TagTypes.CashRaiseSchedule, id: account_id },
        TagTypes.CashRaise,
        { type: TagTypes.CashRaise, id: account_id },
        { type: TagTypes.Accounts, id: account_id },
        TagTypes.AccountHoldings,
      ],
    }),

    deleteCashRaiseSchedule: builder.mutation<
      DeleteCashRaiseScheduleResponse,
      {
        account_id: string
        schedule_id: string
        // Truncate the series from this occurrence ("delete this and all
        // future") instead of ending the whole schedule.
        occurrence_date?: string
        // Also cancel this one activated instance in the same transaction,
        // reversing its transfer + reservation. The scoped delete skips
        // activated rows, so an activated clicked occurrence needs this.
        cancel_instance_id?: string
      }
    >({
      query: ({
        account_id,
        schedule_id,
        occurrence_date,
        cancel_instance_id,
      }) => ({
        url: `account/${account_id}/cash-raise-schedule/${schedule_id}/`,
        method: 'DELETE',
        params: { occurrence_date, cancel_instance_id },
      }),
      invalidatesTags: (_result, _error, { account_id }) => [
        TagTypes.CashRaiseSchedule,
        { type: TagTypes.CashRaiseSchedule, id: account_id },
        TagTypes.CashRaise,
        { type: TagTypes.CashRaise, id: account_id },
        // Canceling an activated instance returns its reserved cash to the
        // source sleeve, so per-sleeve balances change — mirror activateCashRaise.
        { type: TagTypes.Accounts, id: account_id },
        TagTypes.AccountHoldings,
      ],
    }),

    // Read-only projection of a proposed cadence (total / remaining / last-date
    // / next-dates) so the create/edit dialog can show an unambiguous preview.
    // A query, not a mutation: it writes nothing and its result is cached per
    // request body as the user tweaks the cadence.
    previewCashRaiseSchedule: builder.query<
      CashRaiseSchedulePreviewResponse,
      { account_id: string; body: CashRaiseSchedulePreviewRequest }
    >({
      query: ({ account_id, body }) => ({
        url: `account/${account_id}/cash-raise-schedule/preview/`,
        method: 'POST',
        body,
      }),
      // An edit-mode preview sends carry_terminator instead of a terminator, so
      // its projection reads the predecessor's REMAINING terminator — which a
      // scoped delete truncates in place. Same args, different answer, so the
      // cache has to follow the schedule tag.
      providesTags: (_result, _error, { account_id }) => [
        { type: TagTypes.CashRaiseSchedule, id: account_id },
      ],
    }),

    // The create/edit dialog's Repeat dropdown, derived from the picked due
    // date: which recurrence patterns that date can anchor, each with its copy
    // and the anchor to send back. Server-owned because the answer depends on
    // the trading calendar (an nth trading-day anchor exists only for a trading
    // day near a month boundary), so the client holds no recurrence labels.
    // schedule_id is edit-mode only and guarantees the stored pattern is in the
    // list even when the due date no longer produces it.
    getCashRaiseRecurrenceOptions: builder.query<
      CashRaiseRecurrenceOptionsResponse,
      { account_id: string; due_date: string; schedule_id?: string }
    >({
      query: ({ account_id, due_date, schedule_id }) => ({
        url: `account/${account_id}/cash-raise-schedule/recurrence-options/`,
        method: 'GET',
        params: { due_date, schedule_id },
      }),
      providesTags: (_result, _error, { account_id }) => [
        { type: TagTypes.CashRaiseSchedule, id: account_id },
      ],
    }),

    createCashTransfer: builder.mutation<
      CreateCashTransferResponse,
      { account_id: string; body: CreateCashTransferRequest }
    >({
      query: ({ account_id, body }) => ({
        url: `account/${account_id}/cash-transfer/`,
        method: 'POST',
        body,
      }),
      // Sleeve cash balances re-poll via SORM (useAccountSleeves), but the
      // legacy per-account RTK caches (account detail + holdings) need an
      // explicit nudge so anything else on the page reflects the new state.
      invalidatesTags: (_result, _error, { account_id }) => [
        { type: TagTypes.Accounts, id: account_id },
        TagTypes.AccountHoldings,
      ],
    }),

    createCashRebalance: builder.mutation<
      CreateCashRebalanceResponse,
      { body: CreateCashRebalanceRequest }
    >({
      // Top-level route (NOT under account/{id}); a single account is just a
      // one-element `account_ids` list.
      query: ({ body }) => ({
        // No trailing slash — the accounts-api route is registered as
        // `/v1/cash-rebalance` exactly; a trailing slash proxies through to a 404.
        url: 'cash-rebalance',
        method: 'POST',
        body,
      }),
      // accounts-api serializes `rebalanced`/`skipped` from Go slices; a nil
      // slice marshals to JSON `null` (not `[]`). Normalize to arrays so every
      // consumer can safely read `.length`/`.map` — an all-skipped run (e.g. a
      // negative-cash account) otherwise crashes the results dialog at render.
      transformResponse: normalizeCashRebalanceResponse,
      // Rebalancing writes legs across each affected account, changing per-sleeve
      // cash balances. Sleeve balances re-poll via SORM (useAccountSleeves), but
      // nudge the legacy per-account caches + holdings so the rest of the page
      // reflects the new state.
      invalidatesTags: (_result, _error, { body }) => [
        ...body.account_ids.map((id) => ({
          type: TagTypes.Accounts as const,
          id,
        })),
        TagTypes.AccountHoldings,
      ],
    }),

    redistributeCashRaises: builder.mutation<
      RedistributeCashRaisesResponse,
      { body: RedistributeCashRaisesRequest }
    >({
      // Top-level route (NOT under account/{id}); a single account is just a
      // one-element `account_ids` list. No trailing slash — the accounts-api
      // route is `/v1/cash-raise/redistribute` exactly.
      query: ({ body }) => ({
        url: 'cash-raise/redistribute',
        method: 'POST',
        body,
      }),
      // Redistribution rewrites an activated raise's per-sleeve debits, so the
      // raise itself and each account's sleeve cash both change. Sleeve balances
      // re-poll via SORM; nudge the legacy per-account caches + holdings too.
      invalidatesTags: (_result, _error, { body }) => [
        TagTypes.CashRaise,
        ...body.account_ids.flatMap((id) => [
          { type: TagTypes.CashRaise as const, id },
          { type: TagTypes.Accounts as const, id },
        ]),
        TagTypes.AccountHoldings,
      ],
    }),

    // Dry-run of redistributeCashRaises. A mutation, not a query: it POSTs the
    // account set and writes nothing, so there is nothing to cache or invalidate.
    previewCashRaiseRedistribution: builder.mutation<
      RedistributePreviewResponse,
      { body: RedistributeCashRaisesRequest }
    >({
      query: ({ body }) => ({
        url: 'cash-raise/redistribute/preview',
        method: 'POST',
        body,
      }),
    }),

    // Per-account cash rebalance: preview what a rebalance would do, then apply
    // the end state the user confirmed or hand-edited.
    getCashRebalancePreview: builder.query<
      CashRebalancePreviewResponse,
      { account_id: string }
    >({
      query: ({ account_id }) => ({
        url: `account/${account_id}/cash-rebalance/preview/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { account_id }) => [
        { type: TagTypes.Accounts, id: account_id },
      ],
    }),

    createManualCashRebalance: builder.mutation<
      ManualCashRebalanceResponse,
      { account_id: string; body: ManualCashRebalanceRequest }
    >({
      query: ({ account_id, body }) => ({
        url: `account/${account_id}/cash-rebalance/`,
        method: 'POST',
        body,
      }),
      // Writes legs that change per-sleeve cash. Sleeve balances re-poll via
      // SORM, but nudge the per-account caches + holdings like the bulk
      // rebalance does so the rest of the page reflects the new state.
      invalidatesTags: (_result, _error, { account_id }) => [
        { type: TagTypes.Accounts, id: account_id },
        TagTypes.AccountHoldings,
      ],
    }),

    // Sleeve realignment (inter-sleeve drift correction)
    getSleeveRealignmentPreview: builder.query<
      SleeveRealignmentPreview,
      { account_id: string; source_scope?: SleeveRealignmentSourceScope }
    >({
      // Always sends the scope: the accounts handler defaults to `over_max`,
      // and every frontend surface wants `over_target`.
      query: ({ account_id, source_scope = DEFAULT_SOURCE_SCOPE }) => ({
        url: `account/${account_id}/sleeve-realignment/preview/?source_scope=${source_scope}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { account_id }) => [
        { type: TagTypes.SleeveRealignment, id: account_id },
      ],
    }),

    initiateSleeveRealignment: builder.mutation<
      SleeveRealignmentBulkActionResponse,
      { body: SleeveRealignmentInitiateRequest }
    >({
      // Top-level route, not under account/{id}. No trailing slash — the proxy
      // forwards the path as-is and accounts-api registers it without one.
      query: ({ body }) => ({
        url: 'sleeve-realignment/initiate',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { body }) =>
        realignmentActionTags(body.items.map((item) => item.account_id)),
    }),

    listSleeveRealignments: builder.query<
      SleeveRealignmentStatus[],
      { account_id: string }
    >({
      query: ({ account_id }) => ({
        url: `account/${account_id}/sleeve-realignment/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { account_id }) => [
        { type: TagTypes.SleeveRealignment, id: account_id },
      ],
    }),

    getSleeveRealignmentCancelPreview: builder.query<
      SleeveRealignmentCancelPreview,
      { account_id: string; realignment_id: string }
    >({
      query: ({ account_id, realignment_id }) => ({
        url: `account/${account_id}/sleeve-realignment/${realignment_id}/cancel-preview/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { account_id }) => [
        { type: TagTypes.SleeveRealignment, id: account_id },
      ],
    }),

    getSleeveRealignmentRebalancePreview: builder.query<
      SleeveRealignmentRebalancePreview,
      { account_id: string; realignment_id: string }
    >({
      query: ({ account_id, realignment_id }) => ({
        url: `account/${account_id}/sleeve-realignment/${realignment_id}/rebalance-preview/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { account_id }) => [
        { type: TagTypes.SleeveRealignment, id: account_id },
      ],
    }),

    completeSleeveRealignment: builder.mutation<
      SleeveRealignmentBulkActionResponse,
      { body: SleeveRealignmentBulkActionRequest }
    >({
      query: ({ body }) => ({
        url: 'sleeve-realignment/complete',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { body }) =>
        realignmentActionTags(body.account_ids),
    }),

    cancelSleeveRealignment: builder.mutation<
      SleeveRealignmentBulkActionResponse,
      { body: SleeveRealignmentBulkActionRequest }
    >({
      query: ({ body }) => ({
        url: 'sleeve-realignment/cancel',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { body }) =>
        realignmentActionTags(body.account_ids),
    }),

    rebalanceSleeveRealignment: builder.mutation<
      SleeveRealignmentBulkActionResponse,
      { body: SleeveRealignmentBulkActionRequest }
    >({
      query: ({ body }) => ({
        url: 'sleeve-realignment/rebalance',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { body }) =>
        realignmentActionTags(body.account_ids),
    }),

    createRemediationLot: builder.mutation<
      CreateRemediationLotsResponse,
      { body: CreateRemediationLotsRequestBody }
    >({
      query: ({ body }) => ({
        url: 'account/remediation-lots/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        TagTypes.Accounts,
        TagTypes.AccountHoldings,
        TagTypes.Transaction,
      ],
    }),

    deleteRemediationLot: builder.mutation<void, { transactionIds: string[] }>({
      query: ({ transactionIds }) => ({
        url: 'account/remediation-lots/',
        method: 'DELETE',
        body: transactionIds,
      }),
      invalidatesTags: [
        TagTypes.Accounts,
        TagTypes.AccountHoldings,
        TagTypes.Transaction,
      ],
    }),
  }),
})

export const {
  endpoints: {
    getAccountList,
    getAccountIds,
    getAbbreviatedAccountList,
    getAccountById,
    getAccountStatistics,
    cancelAccountOrder,
    getOrders,
    playAccount,
    pauseAccount,
    updateAccount,
    updateAccountSettings,
    getAccountHoldings,
    createSleeve,
    updateSleeve,
    deleteSleeve,
    assignHoldingsToSleeve,
    updateLot,
    assignSleeveToModel,
    unassignSleeveFromModel,
    updateAccountRestrictions,
    updateAccountExtra,
    getAdvisorsFromPositions,
    getAccountsFromPositions,
    getPositionsForInstrument,
    createCashRaise,
    updateCashRaise,
    cancelCashRaise,
    completeCashRaise,
    getProjectedCash,
    activateCashRaise,
    createCashTransfer,
    createCashRebalance,
    getCashRebalancePreview,
    createManualCashRebalance,
    createRemediationLot,
    deleteRemediationLot,
    getAccountStatuses,
  },
  useGetAccountListQuery,
  useGetAccountIdsQuery,
  useLazyGetAccountIdsQuery,
  useGetAbbreviatedAccountListQuery,
  useGetAccountByIdQuery,

  useCreateSleeveMutation,
  useUpdateSleeveMutation,
  useDeleteSleeveMutation,
  useAssignHoldingsToSleeveMutation,
  useReassignTransactionsMutation,
  useBatchReassignTransactionsMutation,
  useUpdateLotMutation,
  useAssignSleeveToModelMutation,
  useUnassignSleeveFromModelMutation,
  useUpdateAccountSettingsMutation,
  useUpdateAccountRestrictionsMutation,
  useSetWashSaleOverrideMutation,
  useBulkUpdateAccountRestrictionsMutation,
  useUpdateAccountExtraMutation,

  useGetPositionsForInstrumentQuery,
  useCreateCashRaiseMutation,
  useCreateCashTransferMutation,
  useCreateCashRebalanceMutation,
  useGetCashRebalancePreviewQuery,
  useCreateManualCashRebalanceMutation,
  useUpdateCashRaiseMutation,
  useCancelCashRaiseMutation,
  useCompleteCashRaiseMutation,
  useGetProjectedCashQuery,
  useGetSleeveRealignmentPreviewQuery,
  useInitiateSleeveRealignmentMutation,
  useListSleeveRealignmentsQuery,
  useGetSleeveRealignmentCancelPreviewQuery,
  useGetSleeveRealignmentRebalancePreviewQuery,
  useCompleteSleeveRealignmentMutation,
  useCancelSleeveRealignmentMutation,
  useRebalanceSleeveRealignmentMutation,
  useRedistributeCashRaisesMutation,
  usePreviewCashRaiseRedistributionMutation,
  useActivateCashRaiseMutation,
  useCreateRemediationLotMutation,
  useDeleteRemediationLotMutation,
  useCreateCashRaiseScheduleMutation,
  useEditCashRaiseScheduleMutation,
  usePreviewCashRaiseScheduleQuery,
  useGetCashRaiseRecurrenceOptionsQuery,
  useDeleteCashRaiseScheduleMutation,

  useGetAccountStatusesQuery,
} = accountsSlice
